/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { RequestScope, ScopeType } from '../actions/api';
import { SurveyScope, ScopeGroups } from '@g17eco/types/surveyCommon';
import {
  DataShareMin,
  DataShareStatus,
  RestrictionType,
} from '../types/dataShare';
import { handleRouteError } from '../logger';
import { RequestReviewMethod } from '../types/initiative';
import { InitiativeCompany } from '@g17eco/types/initiativeSurvey';
import { SURVEY } from '@constants/terminology';
import { getDefaultScope, getModulesFromScope } from './survey-scope';
import { CombinedDataScopeAccess, DataScope, DataScopeAccess, DataShareScopeView } from '../types/surveyDataScope';

export const MESSAGE = {
  NO_ACCESS: 'Data sharing is turned off. All requests from Portfolio Tracker organisations are automatically rejected.',
  NO_REQUEST: 'You have no data sharing requests.',
  SHARE_NOTE: `Only information from ${SURVEY.PLURAL} that are marked as 'Complete' is shared. Data marked as 'Private' is never shared.`
}

// Moving forward should only use combinedDataScopeAccess and no longer optional
export const getDataShareScopeConstraints = (
  dataShares: DataShareMin[],
  combinedDataScopeAccess: CombinedDataScopeAccess | undefined
): { access: DataScopeAccess, scopeConstraint: Set<string> } => {

  const scopeConstraint = new Set<string>();

  if (combinedDataScopeAccess) {
    const access = combinedDataScopeAccess.access;

    if (access === DataScopeAccess.Full) {
      return { scopeConstraint, access };
    }
    if (access === DataScopeAccess.None) {
      return { scopeConstraint, access };
    }

    if (access === DataScopeAccess.Partial) {
      if (combinedDataScopeAccess.scope) {
        return {
          scopeConstraint: new Set(Object.values(combinedDataScopeAccess.scope).flat()),
          access: access,
        };
      }
      handleRouteError(new Error('Missing required dataScope scope for partial access'), {
        combinedDataScopeAccess,
        dataShares,
      })
    }
  }

  // TODO [DATA-SHARE] Old way, for backward compatability, remove moving forward
  for (const dataShare of dataShares) {
    const scope = dataShare.dataScope?.survey?.scope;
    const dataScopeAccess = dataShare.dataScope?.survey?.access;

    if (dataScopeAccess === DataScopeAccess.None) {
      continue; // Ignore it
    }

    // Old missing scope still supported...
    if (!scope || dataScopeAccess === DataScopeAccess.Full) {
      // Unrestricted access, clear
      scopeConstraint.clear();
      return { access: DataScopeAccess.Full, scopeConstraint };
    }

    Object.values(scope)
      .flat()
      .forEach((code) => scopeConstraint.add(code));
  }

  return {
    scopeConstraint,
    access: scopeConstraint.size === 0 ? DataScopeAccess.None : DataScopeAccess.Partial,
  };
};

export const isViewShared = (
  company: Pick<InitiativeCompany, 'type' | 'requestedDataShares'>,
  view: DataShareScopeView
) => {
  const dataShares = company.requestedDataShares ?? [];
  return dataShares.some((dataShare) => dataShare.status === DataShareStatus.Accepted && (dataShare.dataScope?.survey?.views ?? []).includes(view));
};

export const getViewAccess = (company: Pick<InitiativeCompany, 'requestedDataShares'>) => {
  const mergedScopeViews = mergeScopeViews(getAcceptedDataShares(company.requestedDataShares));
  const numbOfScopeViews = Object.keys(DataShareScopeView).length;
  if (mergedScopeViews.length === numbOfScopeViews) {
    return DataScopeAccess.Full;
  }
  if (mergedScopeViews.length > 0) {
    return DataScopeAccess.Partial;
  }
  return DataScopeAccess.None;
};

export const isNoAccess = (company: Pick<InitiativeCompany, 'dataShare'>) => company.dataShare?.requestReview === RequestReviewMethod.NoAccess;

export const canAccessData = (dataScope: DataScope | undefined) => {
  return ([DataScopeAccess.Full, DataScopeAccess.Partial] as DataScopeAccess[]).includes(getScopeAccess(dataScope));
}

export const hasFullScopeAccess = (company: Pick<InitiativeCompany, 'requestedDataShares'>) => {
  const dataShares = company.requestedDataShares ?? [];
  return dataShares.some((dataShare) => dataShare.status === DataShareStatus.Accepted && getScopeAccess(dataShare.dataScope) === DataScopeAccess.Full);
};

export const getScopeAccess = (dataScope: DataScope | undefined) => {
  if (!dataScope) {
    return DataScopeAccess.None;
  }
  if (!dataScope.survey) {
    return DataScopeAccess.Full;
  }
  if (dataScope.survey.access) {
    return dataScope.survey.access;
  }
  if (!dataScope.survey.scope) {
    return DataScopeAccess.Full
  }
  return DataScopeAccess.Partial;
}

export const getAcceptedDataShares = (requestedDataShares: DataShareMin[] = []) => {
  return requestedDataShares.filter((item) => item.status === DataShareStatus.Accepted);
};

export const isRestricted = (dataShare: DataShareMin) => {
  if (!dataShare.restriction || dataShare.restriction.type === RestrictionType.NotRestricted) {
    return false;
  }
  return !dataShare.restriction.endDate || new Date(dataShare.restriction.endDate) > new Date();
}

export const getScopeViews = (dataScope: DataScope | undefined) => dataScope?.survey?.views ?? [];

export const mergeScopeViews = (dataShare: DataShareMin[]): DataShareScopeView[] => {
  const mergedViews = new Set<DataShareScopeView>();

  dataShare.forEach(share => {
    const shareViews = getScopeViews(share.dataScope);
    shareViews.forEach(v => {
      mergedViews.add(v);
    })
  });

  return Array.from(mergedViews);
}

export const mergeScopes = (scope: Partial<SurveyScope>, requestScopes: RequestScope[], action: 'add' | 'remove' = 'add'): SurveyScope => {
  const mergedScope = {
    [ScopeGroups.Sdg]: new Set(scope[ScopeGroups.Sdg]),
    [ScopeGroups.Materiality]: new Set(scope[ScopeGroups.Materiality]),
    [ScopeGroups.Standards]: new Set(scope[ScopeGroups.Standards]),
    [ScopeGroups.Frameworks]: new Set(scope[ScopeGroups.Frameworks]),
    [ScopeGroups.Custom]: new Set(scope[ScopeGroups.Custom]),
  }
  requestScopes.forEach(reqScope => {
    if (action === 'add') {
      mergedScope[reqScope.scopeType as ScopeGroups].add(reqScope.code)
    } else {
      mergedScope[reqScope.scopeType as ScopeGroups].delete(reqScope.code)
    }
  })
  return {
    [ScopeGroups.Sdg]: Array.from(mergedScope[ScopeGroups.Sdg]),
    [ScopeGroups.Materiality]: Array.from(mergedScope[ScopeGroups.Materiality]),
    [ScopeGroups.Standards]: Array.from(mergedScope[ScopeGroups.Standards]),
    [ScopeGroups.Frameworks]: Array.from(mergedScope[ScopeGroups.Frameworks]),
    [ScopeGroups.Custom]:  Array.from(mergedScope[ScopeGroups.Custom]),
  };
};

export const getScopeOptions = (dataScope: DataScope | undefined): RequestScope[] => {
  const { standards = [], frameworks = [], custom = [] } = dataScope?.survey?.scope ?? ({} as SurveyScope);
  const getOptions = (packs: string[], scopeType: ScopeType) => packs.map((item) => ({ code: item, scopeType }));
  return [
    ...getOptions(standards, 'standards'),
    ...getOptions(frameworks, 'frameworks'),
    ...getOptions(custom, 'custom'),
  ];
};

export const getScopeModules = (dataScope: DataScope | undefined) => {
  if (!dataScope) {
    return [];
  }
  const scope = dataScope.survey?.scope ?? {};
  return getModulesFromScope(scope);
}

export const combinedScope = (dataShares: DataShareMin[]): SurveyScope => {
  let combinedScope = getDefaultScope();

  dataShares.forEach(share => {
    const scopeOptions = getScopeOptions(share.dataScope);
    combinedScope = mergeScopes(combinedScope, scopeOptions);
  });
  return combinedScope;
}

export const combinedScopeAccess = (dataShares: DataShareMin[]): DataScopeAccess => {
  const listSharedScopeAccess = dataShares.map(share => getScopeAccess(share.dataScope));

  const hasFullScopeAccess = listSharedScopeAccess.some(access => access === DataScopeAccess.Full);
  if (hasFullScopeAccess) {
    return DataScopeAccess.Full;
  }

  const hasPartialScopeAccess = listSharedScopeAccess.some(access => access === DataScopeAccess.Partial);
  if (hasPartialScopeAccess) {
    return DataScopeAccess.Partial;
  }

  return DataScopeAccess.None;
}

export const getDataScopeSurvey = (dataScope: DataScope | undefined) => {
  return dataScope?.survey ?? {};
};

export const convertScope = (selectedScopes: RequestScope[]) => {
  return selectedScopes.reduce(
    (acc, cur) => {
      switch (cur.scopeType) {
        case 'standards':
          acc.standards.push(cur.code);
          break;
        case 'frameworks':
          acc.frameworks.push(cur.code);
          break;
        case 'custom':
          acc.custom.push(cur.code);
          break;
        case 'sdg':
          acc.sdg.push(cur.code);
          break;
        case 'materiality':
          acc.materiality.push(cur.code);
          break;
        default:
          break;
      }
      return acc;
    },
    { standards: [], frameworks: [], custom: [], sdg: [], materiality: [] } as SurveyScope
  );
};

const getAllViews = () => (Object.values(DataShareScopeView) as DataShareScopeView[]);

export const getDefaultRequestDataScope = (): DataScope => ({
  survey: {
    scope: undefined,
    views: getAllViews(),
    access: DataScopeAccess.Full,
  },
});

export const detectChangesDataShareRequest = (oldDataScope: DataScope | undefined, updateDataScope: DataScope) => {
  // When request a new data share
  if (!oldDataScope) {
    return true;
  }

  // When change scope views
  const oldScopeViews = getScopeViews(oldDataScope);
  const updateScopeViews = getScopeViews(updateDataScope);
  if (updateScopeViews.length !== oldScopeViews.length || updateScopeViews.some(v => !oldScopeViews.includes(v))) {
    return true;
  }

  // When change scope modules
  const oldScopes = getScopeModules(oldDataScope);
  const updateScopes = getScopeModules(updateDataScope);
  if (updateScopes.length !== oldScopes.length || updateScopes.some(scope => !oldScopes.includes(scope))) {
    return true;
  }

  return false;
}

const ACCESS_TEXT_MAP = {
  [DataShareScopeView.Insights]: 'Insights',
  [DataShareScopeView.Downloads]: 'Downloads',
  [DataShareScopeView.Survey]: `Full ${SURVEY.CAPITALIZED_SINGULAR} access`,
  [DataShareScopeView.Messaging]: 'Messaging',
};

export const getAccessHint = (company: InitiativeCompany) => {
  const shareScopeViewsAccess = getViewAccess(company);

  if (shareScopeViewsAccess === DataScopeAccess.None) {
    return (
      <div className='p-2'>
        <span>Not shared:</span>
        <br />
        <span>Insights, Downloads, Full {SURVEY.CAPITALIZED_SINGULAR} access, and Messaging.</span>
      </div>
    );
  }

  if (shareScopeViewsAccess === DataScopeAccess.Full) {
    return (
      <div className='p-2'>
        <span>Company is sharing:</span>
        <br />
        <span>Insights, Downloads, Full {SURVEY.CAPITALIZED_SINGULAR} access, and Messaging.</span>
      </div>
    );
  }

  const mergedScopeViews = mergeScopeViews(getAcceptedDataShares(company.requestedDataShares));

  if (shareScopeViewsAccess === DataScopeAccess.Partial) {
    const sharing = Object.values(DataShareScopeView)
      .filter((item) => mergedScopeViews.includes(item))
      .map((item) => ACCESS_TEXT_MAP[item])
      .join(', ');
    const notShared = Object.values(DataShareScopeView)
      .filter((item) => !mergedScopeViews.includes(item))
      .map((item) => ACCESS_TEXT_MAP[item])
      .join(', ');
    return (
      <div className='p-2'>
        <p className='mb-1'>
          <span>Company is sharing:</span>
          <br />
          <span>{sharing}</span>
        </p>
        <p className='mb-1'>
          <span>Not shared:</span>
          <br />
          <span>{notShared}</span>
        </p>
      </div>
    );
  }
};